<template>
  <div class="relative z-[1] max-w-max" @keydown.esc="isOpen = false">
    <button
      aria-label="More options"
      :aria-expanded="isOpen"
      class="p-8 action-menu-toggle"
      :class="{ active: isOpen, flat: noBackground }"
      @click="toggleOpen"
    >
      <more-icon v-if="iconType === IconType.KEBAB" color="#4B4C52" />
      <menu-icon v-else-if="iconType === IconType.HAMBURGER" color="#4B4C52" />
    </button>
    <ul ref="actionMenu" :class="isOpen ? 'block' : 'hidden'" class="action-menu box-shadow-lg">
      <li v-for="(option, i) in menuOptions" :id="`menuOption-${i}`" :key="`menuOption-${i}`" class="text-subtitle font-medium">
        <a
          :href="option.href"
          :target="option.target"
          @click="
            option.click();
            if (option.closeOnClick) isOpen = false;
          "
        >
          <component
            :is="option.icon"
            :class="option.iconClasses"
            v-bind="{ height: option.height, width: option.width, color: '#4B4C52', class: option.iconClasses }"
          />{{ option.labelText }}
        </a>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import { MenuItemOptions, IconType } from './__types__/ActionMenuTypes';
import MoreIcon from '../../graphics/icons/MoreIcon.vue';
import MenuIcon from '../../graphics/icons/MenuIcon.vue';

export default {
  name: 'ActionMenu',
  components: { MoreIcon, MenuIcon },
  props: {
    menuOptions: {
      type: Array as () => MenuItemOptions[],
      required: true,
    },
    iconType: {
      type: String as PropType<IconType>,
      required: false,
      default: IconType.KEBAB,
      validator(value: IconType) {
        // The value must be one of these strings
        return !value || Object.values(IconType).includes(value);
      },
    },
    noBackground: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      IconType,
      isOpen: false,
    };
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('keydown', this.handleTabNavigation);
  },
  destroyed() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleTabNavigation);
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    },
    handleTabNavigation(event) {
      if (event.key === 'Tab' && this.isOpen) {
        const focusableElements = this.$refs.actionMenu.querySelectorAll('a');

        if (event.shiftKey) {
          if (document.activeElement === focusableElements[0]) {
            this.isOpen = false;
          }
        } else if (document.activeElement === focusableElements[focusableElements.length - 1]) {
          this.isOpen = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.action-menu-toggle {
  border: 1px solid theme('colors.white');
  border-radius: 4px;

  &.flat:not(.active) {
    border: 1px solid transparent;
  }

  &:hover {
    background: theme('colors.grey.background.contrast');

    svg {
      filter: invert(9%) sepia(100%) saturate(5180%) hue-rotate(328deg) brightness(70%) contrast(108%);
    }
  }

  &.active {
    outline: none;
    border: 1px solid #bb0049;
    background: theme('colors.grey.background.contrast');
    color: theme('colors.brand.contrast');

    svg {
      filter: invert(9%) sepia(100%) saturate(5180%) hue-rotate(328deg) brightness(70%) contrast(108%);
    }
  }

  &:focus-visible {
    outline: none;
    border: 1px solid #bb0049;
    color: theme('colors.brand.contrast');

    svg {
      filter: invert(9%) sepia(100%) saturate(5180%) hue-rotate(328deg) brightness(70%) contrast(108%);
    }
  }
}

.action-menu {
  border-radius: 4px;
  position: absolute;
  right: 0;
  background: theme('colors.white');

  svg {
    margin-right: 1rem;
  }

  padding: 1rem 0;

  li {
    display: block;
    width: 100%;
    border: 2px solid theme('colors.white');

    &:hover {
      cursor: pointer;
      border: 2px solid theme('colors.grey.background.contrast');
      background: theme('colors.grey.background.contrast');
      color: theme('colors.brand.contrast');

      svg {
        filter: invert(9%) sepia(100%) saturate(5180%) hue-rotate(328deg) brightness(70%) contrast(108%);
      }
    }

    a {
      padding: 4px 48px 4px 10px;
      width: 100%;
      display: flex;
      white-space: pre;
      box-sizing: border-box;

      &:focus-visible {
        outline: 2px dotted theme('colors.brand.contrast');
        color: theme('colors.brand.contrast');
        background: theme('colors.grey.background.contrast');
        svg {
          filter: invert(9%) sepia(100%) saturate(5180%) hue-rotate(328deg) brightness(70%) contrast(108%);
        }
      }
    }
  }
}
</style>
